// API service for centralized API communication
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
import { navigateToLogin, isOnLoginPage } from '../config/frontend';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.isLoggingOut = false; // Flag to prevent multiple logout events
  }

  // Get authentication headers
  getAuthHeaders(customHeaders = {}) {
    const token = localStorage.getItem('auth_token');
    return {
      'Accept': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...customHeaders
    };
  }

  // Generic API request method
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    // Determine if we need to set Content-Type
    let headers;
    if (options.body instanceof FormData) {
      // For FormData, don't set Content-Type - let browser set it with boundary
      headers = this.getAuthHeaders();
    } else {
      // For JSON, set Content-Type
      headers = this.getAuthHeaders({ 'Content-Type': 'application/json' });
    }
    
    // IMPORTANT: Merge custom headers with auth headers, ensuring auth headers take precedence
    if (options.headers) {
      headers = {
        ...headers,
        ...options.headers,
        // Always keep Authorization header from auth headers
        ...(headers.Authorization && { 'Authorization': headers.Authorization })
      };
    }
    
    const config = {
      ...options,
      headers
    };

    try {
      const response = await fetch(url, config);
      
      if (response.status === 401) {
        // Clear invalid token immediately
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
        
        // Only handle logout if we're not already on login page and not already logging out
        if (!this.isLoggingOut && !isOnLoginPage()) {
          this.isLoggingOut = true;
          
          // Use a more direct approach - redirect immediately instead of event
          navigateToLogin();
          
          // Reset flag after a longer delay
          setTimeout(() => {
            this.isLoggingOut = false;
          }, 5000);
        }
        
        throw new Error('Authentication failed - please login again');
      }
      
      if (!response.ok) {
        // Try to parse error response body to get meaningful error message
        let errorMessage = `HTTP error! status: ${response.status}`;
        let errorDetails = null;
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            errorMessage = errorData.message || errorData.error || errorMessage;
            errorDetails = errorData.errors || errorData.details;
          } else {
            // Response is not JSON (probably HTML error page)
            const text = await response.text();
            if (text.includes('<!DOCTYPE') || text.includes('<html')) {
              errorMessage = `Server returned HTML instead of JSON (status: ${response.status}). This usually means authentication failed or the route is not protected properly.`;
            } else {
              errorMessage = `Server error: ${text.substring(0, 100)}`;
            }
          }
        } catch (parseError) {
          // If parsing fails, use default error message
          errorMessage = `Failed to parse error response (status: ${response.status})`;
        }
        
        const error = new Error(errorMessage);
        if (errorDetails) {
          error.details = errorDetails;
        }
        throw error;
      }
      
      // Parse successful response
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          return await response.json();
        } else {
          // Response is not JSON
          const text = await response.text();
          if (text.includes('<!DOCTYPE') || text.includes('<html')) {
            throw new Error('Server returned HTML instead of JSON. This usually means authentication failed or you were redirected to a login page.');
          }
          throw new Error(`Expected JSON response but got: ${contentType}`);
        }
      } catch (parseError) {
        console.error(`Failed to parse response from ${endpoint}:`, parseError);
        throw new Error('Failed to parse server response. Please try logging in again.');
      }
    } catch (error) {
      // Suppress error logging for known endpoints that may not be configured
      const silentEndpoints = ['/mobile-money-tracking/balance'];
      const shouldLog = !silentEndpoints.some(ep => endpoint.includes(ep));
      
      if (shouldLog) {
        console.error(`API request failed for ${endpoint}:`, error);
      }
      throw error;
    }
  }

  // Generic HTTP methods
  async get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  async post(endpoint, data, options = {}) {
    // Handle FormData differently
    if (data instanceof FormData) {
      return this.request(endpoint, {
        ...options,
        method: 'POST',
        body: data
      });
    } else {
      return this.request(endpoint, {
        ...options,
        method: 'POST',
        body: JSON.stringify(data)
      });
    }
  }

  async put(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async patch(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  async delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }

  // Customer methods
  async getCustomers(queryParams = '') {
    const endpoint = queryParams ? `/customers?${queryParams}` : '/customers';
    return this.request(endpoint);
  }

  async getCustomer(id) {
    return this.request(`/customers/${id}`);
  }

  async addCustomer(customerData) {
    return this.request('/customers', {
      method: 'POST',
      body: JSON.stringify(customerData)
    });
  }

  async updateCustomer(id, customerData) {
    return this.request(`/customers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(customerData)
    });
  }

  async deleteCustomer(id) {
    return this.request(`/customers/${id}`, {
      method: 'DELETE'
    });
  }

  async validateCustomerEmail(email, excludeId = null) {
    const params = new URLSearchParams({ email });
    if (excludeId) params.append('exclude_id', excludeId);
    return this.request(`/customers/validate-email?${params}`);
  }

  async validateCustomerName(name, excludeId = null) {
    const params = new URLSearchParams({ name });
    if (excludeId) params.append('exclude_id', excludeId);
    return this.request(`/customers/validate-name?${params}`);
  }

  // Payment methods
  async getPayments(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.search) queryParams.append('search', params.search);
    if (params.method && params.method !== 'all') queryParams.append('method', params.method);
    if (params.customer_id) queryParams.append('customer_id', params.customer_id);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.page) queryParams.append('page', params.page);
    if (params.per_page) queryParams.append('per_page', params.per_page);
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params.sort_order) queryParams.append('sort_order', params.sort_order);
    
    const queryString = queryParams.toString();
    const endpoint = `/payments${queryString ? `?${queryString}` : ''}`;
    return this.request(endpoint);
  }

  async getPayment(id) {
    return this.request(`/payments/${id}`);
  }

  async addPayment(paymentData) {
    return this.request('/payments', {
      method: 'POST',
      body: JSON.stringify(paymentData)
    });
  }

  async updatePayment(id, paymentData) {
    return this.request(`/payments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(paymentData)
    });
  }

  async deletePayment(id) {
    return this.request(`/payments/${id}`, {
      method: 'DELETE'
    });
  }

  async getPaymentStats(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    
    const queryString = queryParams.toString();
    return this.request(`/payments/stats${queryString ? `?${queryString}` : ''}`);
  }

  async generateNextPaymentId() {
    return this.request('/payments/generate-id');
  }

  // Bulk Sales methods
  async getLastBulkSaleId() {
    return this.request('/bulk-sales/last-id');
  }

  async createBulkSale(bulkSaleData) {
    return this.request('/bulk-sales', {
      method: 'POST',
      body: JSON.stringify(bulkSaleData)
    });
  }

  async getBulkSaleDetails(bulkSaleId) {
    return this.request(`/bulk-sales/${bulkSaleId}`);
  }

  async updateBulkSale(bulkSaleId, bulkSaleData) {
    return this.request(`/bulk-sales/${bulkSaleId}`, {
      method: 'PUT',
      body: JSON.stringify(bulkSaleData)
    });
  }

  async getBulkSalesHistory(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.search) queryParams.append('search', params.search);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.page) queryParams.append('page', params.page);
    if (params.per_page) queryParams.append('per_page', params.per_page);
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params.sort_order) queryParams.append('sort_order', params.sort_order);
    
    const queryString = queryParams.toString();
    return this.request(`/bulk-sales${queryString ? `?${queryString}` : ''}`);
  }

  // Product methods
  async searchProducts(query) {
    return this.request(`/products/search?q=${encodeURIComponent(query)}`);
  }

  async getAllProducts() {
    return this.request('/products/all');
  }

  async getPOSView2Products(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.category_id) queryParams.append('category_id', params.category_id);
    if (params.in_stock_only !== undefined) queryParams.append('in_stock_only', params.in_stock_only);
    
    const queryString = queryParams.toString();
    const endpoint = `/products/pos-view2${queryString ? `?${queryString}` : ''}`;
    return this.request(endpoint);
  }

  async getProducts(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.search) queryParams.append('search', params.search);
    if (params.category_id) queryParams.append('category_id', params.category_id);
    if (params.status) queryParams.append('status', params.status);
    if (params.page) queryParams.append('page', params.page);
    if (params.per_page) queryParams.append('per_page', params.per_page);
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params.sort_order) queryParams.append('sort_order', params.sort_order);
    
    const queryString = queryParams.toString();
    const endpoint = `/products${queryString ? `?${queryString}` : ''}`;
    return this.request(endpoint);
  }

  async getProduct(id) {
    return this.request(`/products/${id}`);
  }

  async createProduct(productData) {
    // Check if productData is FormData (for file uploads)
    if (productData instanceof FormData) {
      return this.request('/products', {
        method: 'POST',
        body: productData,
        headers: {
          // Don't set Content-Type for FormData, let browser set it with boundary
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
    } else {
      return this.request('/products', {
        method: 'POST',
        body: JSON.stringify(productData)
      });
    }
  }

  async updateProduct(id, productData) {
    return this.request(`/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(productData)
    });
  }

  async deleteProduct(id) {
    return this.request(`/products/${id}`, {
      method: 'DELETE'
    });
  }

  // Stock Management methods
  async getStockItems(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.search) queryParams.append('search', params.search);
    if (params.category_id) queryParams.append('category_id', params.category_id);
    if (params.status) queryParams.append('status', params.status);
    if (params.page) queryParams.append('page', params.page);
    if (params.per_page) queryParams.append('per_page', params.per_page);
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params.sort_order) queryParams.append('sort_order', params.sort_order);
    
    const queryString = queryParams.toString();
    return this.request(`/stock${queryString ? `?${queryString}` : ''}`);
  }

  async getStockStats() {
    return this.request('/stock/stats');
  }

  async adjustStock(adjustmentData) {
    return this.request('/stock/adjust', {
      method: 'POST',
      body: JSON.stringify(adjustmentData)
    });
  }

  async bulkAdjustStock(bulkAdjustmentData) {
    return this.request('/stock/bulk-adjust', {
      method: 'POST',
      body: JSON.stringify(bulkAdjustmentData)
    });
  }

  async getPendingAdjustments() {
    return this.request('/stock/pending-adjustments');
  }

  async getApprovedAdjustments() {
    return this.request('/stock/approved-adjustments');
  }

  async approveAdjustment(adjustmentId, action, notes = '') {
    return this.request('/stock/approve-adjustment', {
      method: 'POST',
      body: JSON.stringify({
        adjustment_id: adjustmentId,
        action: action,
        notes: notes
      })
    });
  }

  async bulkApproveAdjustments(adjustmentIds, action, notes = '') {
    return this.request('/stock/bulk-approve-adjustments', {
      method: 'POST',
      body: JSON.stringify({
        adjustment_ids: adjustmentIds,
        action: action,
        notes: notes
      })
    });
  }

  async getAdjustmentReasons() {
    return this.request('/stock/adjustment-reasons');
  }

  async getSupplierFinancialSummary(supplierId, startDate = null, endDate = null) {
    const params = new URLSearchParams({ supplier_id: supplierId });
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    return this.request(`/stock/supplier-financial-summary?${params}`);
  }

  async getLastAdjustmentId() {
    return this.request('/stock/last-adjustment-id');
  }


  // Category methods
  async getCategories(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status);
    if (params.page) queryParams.append('page', params.page);
    if (params.per_page) queryParams.append('per_page', params.per_page);
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params.sort_order) queryParams.append('sort_order', params.sort_order);
    
    const queryString = queryParams.toString();
    const endpoint = `/categories${queryString ? `?${queryString}` : ''}`;
    return this.request(endpoint);
  }

  async getAllCategories() {
    return this.request('/categories-all');
  }

  async getAllCategoriesPublic() {
    return this.request('/categories/public');
  }

  async getCategory(id) {
    return this.request(`/categories/${id}`);
  }

  async createCategory(categoryData) {
    return this.request('/categories', {
      method: 'POST',
      body: JSON.stringify(categoryData)
    });
  }

  async updateCategory(id, categoryData) {
    return this.request(`/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(categoryData)
    });
  }

  async deleteCategory(id) {
    return this.request(`/categories/${id}`, {
      method: 'DELETE'
    });
  }

  async getCategoryStats() {
    return this.request('/categories-stats');
  }

  async getCategoryStatsPublic() {
    return this.request('/categories/stats/public');
  }

  // Supplier methods
  async getSuppliers(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status);
    if (params.page) queryParams.append('page', params.page);
    if (params.per_page) queryParams.append('per_page', params.per_page);
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params.sort_order) queryParams.append('sort_order', params.sort_order);
    
    const queryString = queryParams.toString();
    const endpoint = `/suppliers${queryString ? `?${queryString}` : ''}`;
    return this.request(endpoint);
  }

  async getSupplier(supplierId) {
    return this.request(`/suppliers/${supplierId}`);
  }

  async createSupplier(supplierData) {
    return this.request('/suppliers', {
      method: 'POST',
      body: JSON.stringify(supplierData)
    });
  }

  async updateSupplier(supplierId, supplierData) {
    return this.request(`/suppliers/${supplierId}`, {
      method: 'PUT',
      body: JSON.stringify(supplierData)
    });
  }

  async deleteSupplier(supplierId) {
    return this.request(`/suppliers/${supplierId}`, {
      method: 'DELETE'
    });
  }

  async searchSuppliers(query) {
    return this.request(`/suppliers/search?q=${encodeURIComponent(query)}`);
  }

  async getSupplierStats() {
    return this.request('/suppliers/stats');
  }

  // Supplier Payment methods
  async getSupplierPayments(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status);
    if (params.payment_method) queryParams.append('payment_method', params.payment_method);
    if (params.date_range) queryParams.append('date_range', params.date_range);
    if (params.page) queryParams.append('page', params.page);
    if (params.per_page) queryParams.append('per_page', params.per_page);
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params.sort_order) queryParams.append('sort_order', params.sort_order);
    
    const queryString = queryParams.toString();
    const endpoint = `/supplier-payments${queryString ? `?${queryString}` : ''}`;
    
    return this.request(endpoint);
  }

  async createSupplierPayment(paymentData) {
    return this.request('/supplier-payments', {
      method: 'POST',
      body: JSON.stringify(paymentData)
    });
  }

  async updateSupplierPayment(paymentId, paymentData) {
    return this.request(`/supplier-payments/${paymentId}`, {
      method: 'PUT',
      body: JSON.stringify(paymentData)
    });
  }

  async deleteSupplierPayment(paymentId) {
    return this.request(`/supplier-payments/${paymentId}`, {
      method: 'DELETE'
    });
  }

  async getSupplierPaymentStats() {
    return this.request('/supplier-payments/stats');
  }

  async getNextPaymentId(date) {
    return this.request(`/supplier-payments/generate-id?date=${date}`);
  }

  async getSupplierOutstandingBalance(supplierId) {
    // Get supplier's outstanding balance
    return this.request(`/suppliers/${supplierId}/outstanding-balance`);
  }

  async getAllSuppliers() {
    // Try the standard suppliers endpoint first
    try {
      return this.request('/suppliers?per_page=1000');
    } catch (error) {
      // Fallback to suppliers-all if the standard endpoint doesn't work
      return this.request('/suppliers-all');
    }
  }


  // Bulk Reports methods
  async getBulkReportsDashboard(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    
    const queryString = queryParams.toString();
    return this.request(`/reports/bulk/dashboard${queryString ? `?${queryString}` : ''}`);
  }

  async getBulkReportsSales(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    
    const queryString = queryParams.toString();
    return this.request(`/reports/bulk/sales${queryString ? `?${queryString}` : ''}`);
  }

  async getBulkReportsProducts(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    
    const queryString = queryParams.toString();
    return this.request(`/reports/bulk/products${queryString ? `?${queryString}` : ''}`);
  }

  async getBulkReportsCustomers(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    
    const queryString = queryParams.toString();
    return this.request(`/reports/bulk/customers${queryString ? `?${queryString}` : ''}`);
  }

  // Proforma Invoice methods
  async getProformaInvoices(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.page) queryParams.append('page', params.page);
    if (params.per_page) queryParams.append('per_page', params.per_page);
    
    const queryString = queryParams.toString();
    return this.request(`/proforma-invoices${queryString ? `?${queryString}` : ''}`);
  }

  async createProformaInvoice(proformaData) {
    return this.request('/proforma-invoices', {
      method: 'POST',
      body: JSON.stringify(proformaData)
    });
  }

  async getProformaInvoice(id) {
    return this.request(`/proforma-invoices/${id}`);
  }

  async getProformaInvoiceDetails(id) {
    return this.request(`/proforma-invoices/${id}`);
  }

  async updateProformaInvoice(id, proformaData) {
    return this.request(`/proforma-invoices/${id}`, {
      method: 'PUT',
      body: JSON.stringify(proformaData)
    });
  }

  async deleteProformaInvoice(id) {
    return this.request(`/proforma-invoices/${id}`, {
      method: 'DELETE'
    });
  }

  async convertProformaToInvoice(id) {
    return this.request(`/proforma-invoices/${id}/convert`, {
      method: 'POST'
    });
  }

  async getProformaCustomers() {
    return this.request('/proforma-invoices/customers');
  }

  async searchProformaProducts(query) {
    return this.request(`/proforma-invoices/products/search?query=${encodeURIComponent(query)}`);
  }

  async getLastProformaId() {
    return this.request('/proforma-invoices/last-id');
  }

  // Proforma Report methods
  async getProformaSummary(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    
    const queryString = queryParams.toString();
    return this.request(`/reports/proforma/summary${queryString ? `?${queryString}` : ''}`);
  }

  async getProformaAnalytics(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    
    const queryString = queryParams.toString();
    return this.request(`/reports/proforma/analytics${queryString ? `?${queryString}` : ''}`);
  }

  async getProformaInvoicesReport(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.status) queryParams.append('status', params.status);
    if (params.page) queryParams.append('page', params.page);
    if (params.per_page) queryParams.append('per_page', params.per_page);
    
    const queryString = queryParams.toString();
    return this.request(`/reports/proforma/invoices${queryString ? `?${queryString}` : ''}`);
  }

  async exportProformaPdf(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.status) queryParams.append('status', params.status);
    
    const queryString = queryParams.toString();
    return this.request(`/reports/proforma/export-pdf${queryString ? `?${queryString}` : ''}`);
  }

  async exportProformaExcel(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.status) queryParams.append('status', params.status);
    
    const queryString = queryParams.toString();
    return this.request(`/reports/proforma/export-excel${queryString ? `?${queryString}` : ''}`);
  }

  // Credit Note methods
  async getCreditNotes(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.type && params.type !== 'all') queryParams.append('type', params.type);
    if (params.customer_id) queryParams.append('customer_id', params.customer_id);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.page) queryParams.append('page', params.page);
    if (params.per_page) queryParams.append('per_page', params.per_page);
    
    const queryString = queryParams.toString();
    return this.request(`/credit-notes${queryString ? `?${queryString}` : ''}`);
  }

  async getCreditNote(id) {
    return this.request(`/credit-notes/${id}`);
  }

  async createCreditNote(creditNoteData) {
    return this.request('/credit-notes', {
      method: 'POST',
      body: JSON.stringify(creditNoteData)
    });
  }

  async updateCreditNote(id, creditNoteData) {
    return this.request(`/credit-notes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(creditNoteData)
    });
  }

  async deleteCreditNote(id) {
    return this.request(`/credit-notes/${id}`, {
      method: 'DELETE'
    });
  }

  async applyCredit(id, applyData) {
    return this.request(`/credit-notes/${id}/apply-credit`, {
      method: 'POST',
      body: JSON.stringify(applyData)
    });
  }

  async getCreditNoteStats(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    
    const queryString = queryParams.toString();
    return this.request(`/credit-notes/stats${queryString ? `?${queryString}` : ''}`);
  }

  async getCustomersWithCredits() {
    return this.request('/credit-notes/customers-with-credits');
  }

  async getCustomerDocuments(customerId, type = 'all') {
    const queryParams = new URLSearchParams();
    if (type !== 'all') queryParams.append('type', type);
    
    const queryString = queryParams.toString();
    return this.request(`/customers/${customerId}/documents${queryString ? `?${queryString}` : ''}`);
  }

  // Expense methods
  async getExpenses(queryParams = {}) {
    const params = new URLSearchParams();
    Object.keys(queryParams).forEach(key => {
      if (queryParams[key] !== undefined && queryParams[key] !== '') {
        params.append(key, queryParams[key]);
      }
    });
    
    const queryString = params.toString();
    return this.request(`/expenses${queryString ? `?${queryString}` : ''}`);
  }

  async getExpense(id) {
    return this.request(`/expenses/${id}`);
  }

  async createExpense(expenseData) {
    return this.request('/expenses', {
      method: 'POST',
      body: JSON.stringify(expenseData)
    });
  }

  async updateExpense(id, expenseData) {
    return this.request(`/expenses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(expenseData)
    });
  }

  async deleteExpense(id) {
    return this.request(`/expenses/${id}`, {
      method: 'DELETE'
    });
  }

  async getExpenseStats() {
    return this.request('/expenses/stats');
  }

  async getExpenseCategories() {
    return this.request('/expenses/categories');
  }

  // Cash Analysis methods
  async getCashAnalysis(date = null) {
    const params = date ? `?date=${date}` : '';
    return this.request(`/cash-analysis${params}`);
  }

  async saveCashAnalysis(analysisData) {
    return this.request('/cash-analysis', {
      method: 'POST',
      body: JSON.stringify(analysisData)
    });
  }

  async updateCashDenomination(denominationData) {
    return this.request('/cash-analysis/denomination', {
      method: 'PUT',
      body: JSON.stringify(denominationData)
    });
  }

  async addCashTransaction(transactionData) {
    return this.request('/cash-analysis/transaction', {
      method: 'POST',
      body: JSON.stringify(transactionData)
    });
  }

  // Simple payment tracking - updates cash analysis for all payment types
  async trackPayment(paymentData) {
    return this.request('/cash-analysis/transaction', {
      method: 'POST',
      body: JSON.stringify(paymentData)
    });
  }

  // Sync cash expenses to cash analysis
  async syncCashExpensesToAnalysis(date = null) {
    const params = new URLSearchParams();
    if (date) params.append('date', date);
    return this.request(`/cash-analysis/sync-expenses?${params.toString()}`);
  }

  async getCashAuditTrail(analysisId) {
    return this.request(`/cash-analysis/audit?analysis_id=${analysisId}`);
  }

  async getCashStats() {
    return this.request('/cash-analysis/stats');
  }

  async getCustomerPaymentsCashBreakdown(date = null) {
    const params = new URLSearchParams();
    if (date) params.append('date', date);
    return this.request(`/cash-analysis/customer-payments-breakdown?${params.toString()}`);
  }

  async getSupplierPaymentsCashBreakdown(date = null) {
    const params = new URLSearchParams();
    if (date) params.append('date', date);
    return this.request(`/cash-analysis/supplier-payments-breakdown?${params.toString()}`);
  }

  async getMobileMoneySales(date = null) {
    const params = new URLSearchParams();
    if (date) params.append('date', date);
    return this.request(`/cash-analysis/mobile-money-sales?${params.toString()}`);
  }

  // Get comprehensive payment summary for cash analysis
  async getPaymentSummary(date = null) {
    const params = new URLSearchParams();
    if (date) params.append('date', date);
    return this.request(`/cash-analysis/payment-summary?${params.toString()}`);
  }

  // Get all payment types breakdown
  async getAllPaymentsBreakdown(date = null) {
    const params = new URLSearchParams();
    if (date) params.append('date', date);
    return this.request(`/cash-analysis/all-payments-breakdown?${params.toString()}`);
  }

  // Reports API methods
  async getSalesReport(startDate, endDate) {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    return this.request(`/reports/sales?${params.toString()}`);
  }

  async getInventoryReport(category = 'all', supplier = 'all') {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (supplier) params.append('supplier', supplier);
    return this.request(`/reports/inventory?${params.toString()}`);
  }

  async getFinancialReport(startDate, endDate) {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    return this.request(`/reports/financial?${params.toString()}`);
  }

  async exportSalesReport(startDate, endDate, format) {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (format) params.append('format', format);
    return this.request(`/reports/sales/export?${params.toString()}`);
  }

  async exportInventoryReport(category, format) {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (format) params.append('format', format);
    return this.request(`/reports/inventory/export?${params.toString()}`);
  }

  async exportFinancialReport(startDate, endDate, format) {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (format) params.append('format', format);
    return this.request(`/reports/financial/export?${params.toString()}`);
  }

  // Bank Account methods
  async getBankAccounts() {
    return this.request('/bank-accounts');
  }

  async createBankAccount(accountData) {
    return this.request('/bank-accounts', {
      method: 'POST',
      body: JSON.stringify(accountData)
    });
  }

  async updateBankAccount(id, accountData) {
    return this.request(`/bank-accounts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(accountData)
    });
  }

  async getBankTransactions(accountId = null, date = null) {
    const params = new URLSearchParams();
    if (accountId) params.append('account_id', accountId);
    if (date) params.append('date', date);
    return this.request(`/bank-transactions?${params.toString()}`);
  }

  async createBankTransaction(transactionData) {
    return this.request('/bank-transactions', {
      method: 'POST',
      body: JSON.stringify(transactionData)
    });
  }

  // Mobile Money Account methods
  async getMobileMoneyAccounts() {
    return this.request('/mobile-money-accounts');
  }

  async createMobileMoneyAccount(accountData) {
    return this.request('/mobile-money-accounts', {
      method: 'POST',
      body: JSON.stringify(accountData)
    });
  }

  async updateMobileMoneyAccount(id, accountData) {
    return this.request(`/mobile-money-accounts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(accountData)
    });
  }

  async getMobileMoneyTransactions(accountId = null, date = null) {
    const params = new URLSearchParams();
    if (accountId) params.append('account_id', accountId);
    if (date) params.append('date', date);
    return this.request(`/mobile-money-transactions?${params.toString()}`);
  }

  async createMobileMoneyTransaction(transactionData) {
    return this.request('/mobile-money-transactions', {
      method: 'POST',
      body: JSON.stringify(transactionData)
    });
  }

  // Mobile Money Tracking methods
  async getMobileMoneyBalance() {
    return this.request('/mobile-money-tracking/balance');
  }

  async getMobileMoneySummary(startDate = null, endDate = null) {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    return this.request(`/mobile-money-tracking/summary?${params.toString()}`);
  }

  async recordCashToMobileMoney(amount, reference = null, description = null) {
    return this.request('/mobile-money-tracking/cash-to-momo', {
      method: 'POST',
      body: JSON.stringify({ amount, reference, description })
    });
  }

  async recordMobileMoneyToCash(amount, reference = null, description = null) {
    return this.request('/mobile-money-tracking/momo-to-cash', {
      method: 'POST',
      body: JSON.stringify({ amount, reference, description })
    });
  }

  async recalculateMobileMoneyBalance() {
    return this.request('/mobile-money-tracking/recalculate-balance', {
      method: 'POST'
    });
  }

  // Settings methods
  async getSettings() {
    return this.request('/settings');
  }

  async saveSettings(settingsData) {
    return this.request('/settings', {
      method: 'POST',
      body: JSON.stringify(settingsData)
    });
  }

  async getSetting(key) {
    return this.request(`/settings/${key}`);
  }

  async updateSetting(key, value, type = 'string', description = null) {
    return this.request(`/settings/${key}`, {
      method: 'PUT',
      body: JSON.stringify({
        value,
        type,
        description
      })
    });
  }

  async deleteSetting(key) {
    return this.request(`/settings/${key}`, {
      method: 'DELETE'
    });
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!localStorage.getItem('auth_token');
  }

  // User Management methods
  async getUsers(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/users${queryString ? `?${queryString}` : ''}`);
  }

  async createUser(userData) {
    return this.request('/users', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  }

  async updateUser(userId, userData) {
    return this.request(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData)
    });
  }

  async deleteUser(userId) {
    return this.request(`/users/${userId}`, {
      method: 'DELETE'
    });
  }

  async getUser(userId) {
    return this.request(`/users/${userId}`);
  }


  async getUserStats() {
    return this.request('/users/stats');
  }

  async verifyUser(userId) {
    return this.request(`/users/${userId}/verify`, {
      method: 'POST'
    });
  }

  async unverifyUser(userId) {
    return this.request(`/users/${userId}/unverify`, {
      method: 'POST'
    });
  }

  async getCurrentUserPermissions(userId) {
    return this.request(`/user/permissions?user_id=${userId}`);
  }

  // Role & Permission Management methods
  async getPermissions() {
    return this.request('/permissions');
  }

  async getPermissionGroups() {
    return this.request('/permissions/groups');
  }

  async getRoles() {
    return this.request('/roles');
  }

  async updateRolePermissions(roleId, permissions) {
    return this.request(`/roles/${roleId}/permissions`, {
      method: 'PUT',
      body: JSON.stringify({ permissions })
    });
  }

  // User validation methods
  async validateUserEmail(email, excludeId = null) {
    const params = new URLSearchParams({ email });
    if (excludeId) params.append('exclude_id', excludeId);
    return this.request(`/validate/user-email?${params}`);
  }

  async validateUserUsername(username, excludeId = null) {
    const params = new URLSearchParams({ username });
    if (excludeId) params.append('exclude_id', excludeId);
    return this.request(`/validate/user-username?${params}`);
  }

  // Note: Held sales are managed by Livewire components via session storage
  // No API endpoints needed for held sales management
}

export default new ApiService();
